import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowDown,
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaImages,
  FaMapMarkerAlt,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

import useScrollToTop from "../../../hooks/useScrollToTop";
import ScrollIndicator from "../../../components/ScrollIndicator/ScrollIndicator";
import Reveal from "../../../components/Reveal/Reveal";

import About from "./About";
import Diaporama from "./Diaporama";
import LastNews from "./LastNews";
import LastGallerie from "./LastGallerie";
import Disciplines from "./Disciplines";
import PartenairesCarousel from "./PartenairesCarousel";
import FormContact from "./FormContact";
import Footer from "./Footer";
import TrialCtaBanner from "./TrialCtaBanner";

import { saveVisite } from "../../../services/visitesServices";
import { getSettings } from "../../../services/settingsServices";

import "./Home.css";
import LastEvents from "./LastEvents";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const DEFAULT_SETTINGS = {
  nom_club: "Judo Royan Saint-Palais",
  header_title: "Bienvenue sur<br />L'Alliance Judo Royan<br/> Saint-Palais-sur-Mer",
  header_subtitle:
    "Venez nombreux découvrir le club, ses cours, ses actualités et ses modalités d’inscription.",
  header1_photo_url: "",
  header2_photo_url: "",
  logo_club: "",
};

function HomePageVitrine() {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [currentImage, setCurrentImage] = useState("");
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        const clubSettings = Array.isArray(response) ? response[0] : response;

        if (!mounted) return;

        const nextSettings = {
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
        };

        setSettings(nextSettings);

        const firstImage =
          nextSettings.header1_photo_url ||
          nextSettings.header2_photo_url ||
          "";

        setCurrentImage(getImageUrl(firstImage));

        if (nextSettings.couleur_principale || nextSettings.primary_color) {
          document.documentElement.style.setProperty(
            "--primary-color",
            nextSettings.couleur_principale || nextSettings.primary_color,
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const heroImages = useMemo(() => {
    return [settings?.header1_photo_url, settings?.header2_photo_url]
      .filter(Boolean)
      .map((image) => getImageUrl(image));
  }, [settings]);

  useEffect(() => {
    if (!heroImages.length) return undefined;

    if (heroImages.length === 1) {
      setCurrentImage(heroImages[0]);
      return undefined;
    }

    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        currentIndex = (currentIndex + 1) % heroImages.length;
        setCurrentImage(heroImages[currentIndex]);
        setFade(true);
      }, 450);
    }, 7000);

    return () => clearInterval(interval);
  }, [heroImages]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");

    if (lastVisitDate !== today) {
      saveVisite();
      localStorage.setItem("lastVisitDate", today);
    }
  }, []);

  const scrollToAbout = () => {
    document.getElementById("about")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleRegisterClick = () => {
    window.location.href = "/inscription";
  };

  const logoUrl = getImageUrl(settings?.logo_club);

  return (
    <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main className="flex-grow">
        <section className="relative isolate flex min-h-screen items-center overflow-hidden pt-28">
          <div className="absolute inset-0 z-0 bg-slate-950" />

          {currentImage ? (
            <div
              className={`absolute inset-0 z-0 bg-cover bg-center transition-all duration-1000 ease-in-out ${
                fade ? "scale-105 opacity-100" : "scale-100 opacity-0"
              }`}
              style={{
                backgroundImage: `url("${currentImage}")`,
              }}
            />
          ) : (
            <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.40),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.32),transparent_36%),linear-gradient(135deg,#020617,#111827)]" />
          )}

          <div className="absolute inset-0 z-[1] bg-gradient-to-br from-slate-950/90 via-slate-950/70 to-slate-950/45" />
          <div className="absolute inset-0 z-[2] bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-25" />

          <div className="absolute left-[-120px] top-28 z-[3] h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
          <div className="absolute bottom-10 right-[-120px] z-[3] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-4 pb-16 pt-8 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
            <Reveal variant="left">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/80 shadow-2xl backdrop-blur-xl">
                <HiSparkles className="text-primary" />
                Site officiel du club
              </div>

              <div className="mt-7 max-w-4xl">
                <h1
                  className="font-barlow text-5xl font-extrabold leading-[0.95] tracking-tight text-white drop-shadow-2xl sm:text-6xl lg:text-7xl xl:text-8xl"
                  dangerouslySetInnerHTML={{
                    __html:
                      settings?.header_title || DEFAULT_SETTINGS.header_title,
                  }}
                />

                <p className="mt-6 max-w-2xl text-base leading-8 text-white/85 drop-shadow sm:text-lg">
                  {settings?.header_subtitle ||
                    DEFAULT_SETTINGS.header_subtitle}
                </p>
              </div>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleRegisterClick}
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-bold text-white shadow-[0_20px_60px_rgba(242,113,49,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  <FaUserPlus />
                  S'inscrire au club
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                </button>

                <button
                  type="button"
                  onClick={scrollToAbout}
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                >
                  En savoir plus
                  <FaArrowDown className="transition group-hover:translate-y-1" />
                </button>
              </div>

              <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaUsers className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    Club
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Vie associative
                  </p>
                </div>

                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaCalendarAlt className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    Cours
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Horaires & tarifs
                  </p>
                </div>

                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaImages className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    Actus
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Photos & nouvelles
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal
              variant="right"
              delay={120}
              className="relative hidden lg:block"
            >
              <div className="relative ml-auto max-w-md">
                <div className="absolute -left-8 -top-8 h-32 w-32 rounded-full bg-primary/30 blur-2xl" />
                <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full bg-blue-500/25 blur-2xl" />

                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-2xl">
                  <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5">
                    <div className="flex items-center gap-4">
                      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl bg-white shadow-xl">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={settings?.nom_club || "Logo du club"}
                            className="h-full w-full object-contain p-2"
                          />
                        ) : (
                          <span className="font-barlow text-2xl font-bold text-primary">
                            MC
                          </span>
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/50">
                          Bienvenue
                        </p>
                        <p className="mt-1 truncate font-barlow text-2xl font-bold text-white">
                          {settings?.nom_club || "MonAppliClub Light"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-4">
                        <FaCheckCircle className="text-primary" />
                        <p className="text-sm font-semibold text-white/85">
                          Une présentation claire pour les familles
                        </p>
                      </div>

                      <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-4">
                        <FaClock className="text-primary" />
                        <p className="text-sm font-semibold text-white/85">
                          Horaires, tarifs et inscriptions accessibles
                        </p>
                      </div>

                      <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-4">
                        <FaMapMarkerAlt className="text-primary" />
                        <p className="text-sm font-semibold text-white/85">
                          Une vitrine responsive et moderne
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {loading && (
            <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-white/80 backdrop-blur-xl">
              Chargement du site...
            </div>
          )}
        </section>

        <About />
        <TrialCtaBanner />

        <section id="diaporama" className="scroll-mt-28">
          <Diaporama />
        </section>

        <section id="cours" className="scroll-mt-28">
          <Disciplines />
        </section>

        <section id="cours" className="scroll-mt-28">
          <LastEvents />
        </section>

        <section id="news" className="scroll-mt-28">
          <LastNews />
        </section>

        <section id="galerie" className="scroll-mt-28">
          <LastGallerie />
        </section>

        <section id="partenaires" className="scroll-mt-28">
          <PartenairesCarousel />
        </section>

        <section id="contact" className="scroll-mt-28">
          <FormContact />
        </section>

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}

export default HomePageVitrine;
